import React from "react"
import * as todoApi from "../../todos/api"
import { TimeZoneType } from "../../constants/timezones"
import { useTranslations } from "../hooks/use-translations"
import { Option } from "../types"
import { makeLayout } from "./layout"
import { makeTreegridTodoRow, makeTreegridTodoRows } from "./rows"
import { TodoColumnName, TodoListColumnName, TreeGridTodo } from "./types"
import {
  createTreegrid,
  destroyTreegrid,
  getSummaryTextWithRowCount,
  syncDataFromServerToGrid,
  updateGridData,
} from "../utils/tree-grid"
import { DateTimeService } from "../../services/date-time-service"
import { useRouter } from "../../hooks"
import { useAppContext } from "../../app-context/use-app-context"
import { useContextOptions } from "../../context-options/hooks/use-context-options"
import { TodoViewModel } from "../../todos/api/todo"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { useAddTodoButton } from "../hooks/use-add-todo-button"
import { useAddedRows } from "../hooks/use-added-rows"
import { useTodoMutations } from "../../todos/hooks/use-todo-mutations"
import { useDeleteTodo } from "../../todos/hooks/use-delete-todo"
import { paths } from "../../paths"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { getColorFromColorHtml, mapTranslationsToColor } from "../utils"
import { makeOnFilterFinish } from "../base/event-handlers/on-filter-finish"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"

const id = "__treegrid_todo_list__"

const useTodoList = (props: TodoListProps) => {
  const router = useRouter()
  const { createPathWithContext } = useUrlWithContext()
  // @ts-expect-error
  const todosView = (router.query.view || "currentAndFuture") as TodosViewOptions
  const isProjectTodoListRoute = checkIsProjectTodoListRoute(router.match.path)
  const isTaskTodoListRoute = checkIsTaskTodoListRoute(router.match.path)
  const params = router.match.params as { projectId: string; taskId: string }
  const viewInfo = {
    projectId: isProjectTodoListRoute ? params.projectId : null,
    taskId: isTaskTodoListRoute ? params.taskId : null,
  }
  const { initTodoUpdate } = useTodoMutations()
  const { deleteTodo, status: todoDeletionStatus, resetStatus: resetTodoDeletionStatus } = useDeleteTodo()
  const translations = useTranslations()
  translations.toolbarTodoListSummaryText = getSummaryTextWithRowCount(
    translations.toolbarTodoListSummaryText,
    props.todos.length
  )
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const { disableAddTodoButton, setDisableAddTodoButton, isAddTodoButtonVisible } = useAddTodoButton({
    gridId: id,
    canCreateTodos: props.canCreateTodos,
    translations,
  })
  const { isAddedRow, setAddedRows } = useAddedRows<TreeGridTodo>({ gridId: id })
  const layout = makeLayout({
    ...props,
    id,
    translations,
    isFilterRowVisible: showFilters,
    isAddTodoButtonVisible,
    todosView,
    defaultColumnOrder: props.defaultColumnOrder || todoListDefaultColumnOrder,
    defaultVisibleColumns: props.defaultVisibleColumns || todoListDefaultVisibleColumns,
  })
  const { appContext } = useAppContext()
  const { userMembershipContextOptions } = useContextOptions()

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid) {
      if (todoDeletionStatus.startsWith("deleted")) {
        const todoId = todoDeletionStatus.split(":")[1]
        const updatedRow = { id: todoId, Deleted: 1 }
        const Changes = JSON.stringify({ Changes: [updatedRow] })
        grid.Source.Data.Data.Changes = Changes
        grid.AcceptChanges()
        resetTodoDeletionStatus()
      }
      if (todoDeletionStatus.startsWith("cancelled") || todoDeletionStatus.startsWith("error")) {
        const todoId = todoDeletionStatus.split(":")[1]
        const todoRow = grid.GetRowById(todoId)
        grid.DeleteRow(todoRow, 3) // 3 = unmark row as deleted
        resetTodoDeletionStatus()
      }
    }
  }, [todoDeletionStatus, deleteTodo, resetTodoDeletionStatus])

  // Adding keydown listener to handle ctrl/cmd + c and ctrl/cmd + v for copy paste functionality,
  // and stopping the event propagation so that it doesn't trigger any other copy paste handlers
  // in the app when the treegrid is focused.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "v")) {
        const grid = window.Grids[id]
        if (grid) {
          e.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown, true) // true = capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true)
  }, [])

  // @ts-ignore
  window.Grids["OnFilterToggle"] = function () {
    toggleFilters()
  }

  // @ts-ignore
  window.Grids.OnSearchChange = function (grid: TGrid, value: string) {
    if (grid) {
      grid.SearchExpression = value
      grid.DoSearch("Filter")
    }
  }

  // @ts-ignore
  window.Grids.OnSearchChange = makeOnSearchChange({ translations })
  window.Grids.OnFilterFinish = makeOnFilterFinish({ translations, gridId: "todo-list" })

  window.Grids.OnCanEditDate = function (grid, row: TRow & TreeGridTodo, col, date) {
    return true
  }

  window.Grids.OnValueChanged = function (
    grid: TGrid,
    row: TRow & TreeGridTodo,
    column: TodoColumnName,
    newValue,
    oldValue
  ) {
    // NOTE: Returning early from this function will cause unexpected behavior in the treegrid
    const isEditAllowed = row.CanEdit === 1
    const hasValueChanged = newValue !== oldValue
    const shouldDispatchUpdateRequest = isEditAllowed && hasValueChanged
    if (shouldDispatchUpdateRequest) {
      let value = adaptValue({ column, value: newValue })

      if (column === "dueDate" && value) {
        const dateTimeService = new DateTimeService({
          dateFormat: props.dateFormat,
          timeZone: props.timeZone,
          enableTimeComponent: row.enableTimeComponent === 1,
        })
        const date = new Date(value)
        const adjustedDate = dateTimeService.addTimezoneOffset(date, "UTC")
        value = adjustedDate.toISOString()
      }

      if (column === "ganttBarColor") {
        value = mapTranslationsToColor(translations)[value]
      }

      if (column === "customers" || column === "workspaces" || column === "responsible") {
        // Split the new value string into IDs
        const ids: string[] = newValue.split(";").filter(Boolean)

        // Map IDs to full option objects (id + name)
        const updatedArray = ids.map((id) => {
          const option = props.options.find((opt) => opt.id === id)
          return { id, name: option?.name ?? "" }
        })

        value = updatedArray
      }
      initTodoUpdate(row.id, { field: column, value }).then((updatedTodo) => {
        if (!updatedTodo) return
        const todo = "todo" in updatedTodo ? updatedTodo.todo : updatedTodo
        if (updatedTodo && grid) {
          const currentContext = appContext.subContext || appContext.mainContext
          const userMembershipIds = userMembershipContextOptions.map((m) => m.id)
          const isVisibleInThisView = todo.checkIsVisibleInView(todosView)
          const isVisibleInThisContext = todo.checkIsVisibleInContext(currentContext, userMembershipIds)
          const isVisibleRegardlessOfContext = isTaskTodoListRoute || isProjectTodoListRoute
          const isVisible = isVisibleInThisView && (isVisibleInThisContext || isVisibleRegardlessOfContext)

          if (isVisible) {
            const todoRow = makeTreegridTodoRow({ todo, viewInfo, translations, ...props })
            syncDataFromServerToGrid(grid, [todoRow])
          } else {
            const row = grid.GetRowById(todo.id)
            if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
          }
        }
      })
    }
    return newValue
  }

  window.Grids.OnRowDelete = function (grid: TGrid, row: TRow & TreeGridTodo) {
    deleteTodo(row.id)
  }

  // @ts-expect-error
  window.Grids.OnAddTodo = function (grid: TGrid) {
    const title = "Untitled"
    if (!disableAddTodoButton && grid) {
      setDisableAddTodoButton(true)
      props.onAddTodo({ title }).then((todo) => {
        if (todo) {
          const todoRow = makeTreegridTodoRow({ todo, viewInfo, translations, ...props })
          setAddedRows([{ ...todoRow, focus: true, scrollTo: true }])
          syncDataFromServerToGrid(grid, [{ ...todoRow, Added: 1 }])
          grid.SortRows()
          setDisableAddTodoButton(false)
        }
      })
    }
  }

  window.Grids.OnTip = function (grid: TGrid, row: TRow & TreeGridTodo, column: TodoColumnName) {
    if (column === "completed") {
      return row.completed ? translations.markTodoAsNotCompletedTip : translations.markTodoAsCompletedTip
    }
  }

  window.Grids.OnGetSortValue = function (grid: TGrid, row: TRow & TreeGridTodo, column: TodoColumnName, value) {
    if (column === "dueDate") {
      const shouldSortToTop = isAddedRow(row.id)
      const shouldSortToBottom = !shouldSortToTop && !value
      if (shouldSortToTop) return Number.MIN_SAFE_INTEGER
      if (shouldSortToBottom) return Number.MAX_SAFE_INTEGER
    }
    return value
  }

  // @ts-ignore
  window.Grids.onClickTitleLink = function (link) {
    const linkWithContext = createPathWithContext(link)
    router.history.push(linkWithContext)
  }

  return { layout, todosView, viewInfo }
}

const TodoList = (props: TodoListProps) => {
  const translations = useTranslations()
  const { layout, todosView, viewInfo } = useTodoList(props)

  React.useEffect(() => {
    const todosToShow = filterTodosByView(props.todos, todosView)
    const data = makeTreegridTodoRows({ ...props, todos: todosToShow, viewInfo, translations })
    createTreegrid({ id, layout, data })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const todosToShow = filterTodosByView(props.todos, todosView)
    const data = makeTreegridTodoRows({ ...props, todos: todosToShow, viewInfo, translations })
    updateGridData({ id, data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todosView])

  return (
    <div
      id={id}
      style={{ height: "calc(100vh - 125px)" }}
      data-test="todo-list-container"
      className="todoGrid" /* This classname is required for the plus icon to be hidden in the Panel column */
    ></div>
  )
}

export default TodoList

function adaptValue({ column, value }: { column: string; value: any }) {
  let adaptedValue: any = value
  if (column === "customers" || column === "workspaces" || column === "responsible") {
    adaptedValue = value.split(";").filter(Boolean)
  }
  if (column === "dueDate") {
    if (!value) {
      adaptedValue = null
    } else {
      adaptedValue = new Date(value).toISOString()
    }
  }
  if (column === "completed") {
    if (value === 1) {
      adaptedValue = new Date().toISOString()
    } else {
      adaptedValue = null
    }
  }

  if (column === "enableTimeComponent") {
    adaptedValue = Boolean(value)
  }
  if (column === "ganttBarColor") {
    adaptedValue = getColorFromColorHtml(value)
  }
  return adaptedValue
}

function filterTodosByView(todos: TodoViewModel[], view: TodosViewOptions) {
  if (view === "all") return todos
  if (view === "completed") return todos.filter((t) => t.completed)
  return todos.filter((t) => !t.completed)
}

const todoListDefaultColumnOrder: TodoListColumnName[] = [
  "completed",
  "Panel",
  "title",
  "dueDate",
  "enableTimeComponent",
  "customers",
  "responsible",
  "workspaces",
  "ganttBarColor",
  "description",
]

const todoListDefaultVisibleColumns: TodoColumnName[] = [
  "completed",
  "title",
  "description",
  "dueDate",
  "enableTimeComponent",
  "customers",
  "responsible",
  "workspaces",
]

export type TodosViewOptions = "all" | "currentAndFuture" | "completed"

type TodoListProps = {
  defaultColumnOrder?: TodoListColumnName[]
  defaultVisibleColumns?: TodoColumnName[]
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  timeZone: TimeZoneType
  todos: TodoViewModel[]
  options: Option[]
  canCreateTodos: boolean
  onAddTodo: (newTodo: todoApi.NewTodoData) => Promise<TodoViewModel | void>
}

const checkIsProjectTodoListRoute = (routeMatch: string) => routeMatch === paths.projectTodos()
const checkIsTaskTodoListRoute = (routeMatch: string) => routeMatch === paths.taskTodos()
